<p-dialog
  header="Bulk Edit Items"
  [modal]="true"
  [draggable]="false"
  [style]="{ width: 'min(64rem, 96vw)' }"
  [breakpoints]="{ '575px': '96vw' }"
  appendTo="body"
  [(visible)]="visible"
  [closable]="!saving"
  [closeOnEscape]="!saving"
  (onShow)="onDialogShow()"
>
  <p class="m-0 mb-4 text-sm text-surface-switch-600">
    Editing {{ selectedItems().length }} selected item(s). Enable the fields you
    want to change and set a value for each. Leave batch size empty to clear
    batch pricing.
  </p>
  @if (markupTaxExclusionMessage) {
    <p class="m-0 mb-4 text-sm text-surface-switch-600">
      {{ markupTaxExclusionMessage }}
    </p>
  }

  <div class="grid grid-cols-1 md:grid-cols-[1fr_1fr] gap-6">
    <div class="flex flex-col gap-5">
      @if (!tradeStudyToolFeaturesEnabled()) {
        <div class="flex flex-col gap-2">
          <div class="flex items-center gap-2">
            <p-checkbox
              inputId="bulk-edit-markup"
              [ngModel]="updateMarkup()"
              (ngModelChange)="onUpdateMarkupChange($event)"
              [binary]="true"
            />
            <label
              for="bulk-edit-markup"
              class="text-sm font-medium cursor-pointer"
            >
              Update markup percentage
            </label>
          </div>
          @if (updateMarkup() && selectedItemsWithChildrenCount > 0) {
            <p class="m-0 text-xs text-surface-switch-500">
              Only applies to selected items without subitems ({{
                markupTaxApplicableItemCount
              }}).
            </p>
          }
          @if (updateMarkup()) {
            <p-inputNumber
              inputId="bulk-edit-markup-value"
              [ngModel]="markupPercent()"
              (ngModelChange)="onMarkupPercentChange($event)"
              [showButtons]="true"
              [min]="-999"
              [max]="999"
              suffix="%"
              [minFractionDigits]="0"
              [maxFractionDigits]="2"
              class="w-full"
              inputStyleClass="w-full"
            />
          }
        </div>

        <div class="flex flex-col gap-2">
          <div class="flex items-center gap-2">
            <p-checkbox
              inputId="bulk-edit-tax"
              [ngModel]="updateTax()"
              (ngModelChange)="onUpdateTaxChange($event)"
              [binary]="true"
            />
            <label
              for="bulk-edit-tax"
              class="text-sm font-medium cursor-pointer"
            >
              Update tax code
            </label>
          </div>
          @if (updateTax() && selectedItemsWithChildrenCount > 0) {
            <p class="m-0 text-xs text-surface-switch-500">
              Only applies to selected items without subitems ({{
                markupTaxApplicableItemCount
              }}).
            </p>
          }
          @if (updateTax()) {
            <app-tax-code-select
              [ngModel]="taxPercentage()"
              (ngModelChange)="taxPercentage.set($event)"
              [isNewItem]="false"
              valueOutput="percentage"
              [fullWidth]="true"
              class="block w-full"
            />
          }
        </div>
      }

      <div class="flex flex-col gap-2">
        <div class="flex items-center gap-2">
          <p-checkbox
            inputId="bulk-edit-material"
            [ngModel]="updateMaterial()"
            (ngModelChange)="onUpdateMaterialChange($event)"
            [binary]="true"
          />
          <label
            for="bulk-edit-material"
            class="text-sm font-medium cursor-pointer"
          >
            Update material
          </label>
        </div>
        @if (updateMaterial()) {
          <app-material-select
            [ngModel]="materialId()"
            (ngModelChange)="materialId.set($event)"
            class="block w-full"
          />
        }
      </div>

      <div class="flex flex-col gap-2">
        <div class="flex items-center gap-2">
          <p-checkbox
            inputId="bulk-edit-batch"
            [ngModel]="updateBatchSize()"
            (ngModelChange)="updateBatchSize.set($event)"
            [binary]="true"
          />
          <label
            for="bulk-edit-batch"
            class="text-sm font-medium cursor-pointer"
          >
            Update batch size
          </label>
        </div>
        @if (updateBatchSize()) {
          <p-inputNumber
            inputId="bulk-edit-batch-value"
            [ngModel]="batchSize()"
            (ngModelChange)="batchSize.set($event)"
            [showButtons]="true"
            [min]="1"
            placeholder="Leave empty to clear"
            class="w-full"
            inputStyleClass="w-full"
          />
        }
      </div>
    </div>

    @if (showPreviewChanges()) {
      <div
        class="md:border-l md:border-t-0 border-t border-surface-switch-200 md:pl-6 pt-4 md:pt-0"
      >
        <button
          type="button"
          class="flex items-center gap-2 p-0 m-0 bg-transparent border-0 cursor-pointer text-sm font-medium text-surface-switch-900"
          (click)="togglePreviewExpanded()"
          [attr.aria-expanded]="previewExpanded"
        >
          <i
            class="pi text-xs"
            [class.pi-chevron-down]="previewExpanded"
            [class.pi-chevron-right]="!previewExpanded"
            aria-hidden="true"
          ></i>
          Preview Changes
        </button>

        @if (previewExpanded) {
          @let preview = previewData();
          <div class="mt-3 flex flex-col gap-4 text-sm text-surface-switch-700">
            <p class="m-0">
              You are about to change
              {{ preview.affectedCount }}
              {{ preview.affectedCount === 1 ? "item" : "items" }}.
            </p>

            @for (field of preview.fields; track field.key) {
              <div
                class="flex flex-col gap-2 py-3 first:pt-0 last:pb-0 border-b border-surface-switch-200 last:border-b-0"
              >
                <div class="font-medium text-surface-switch-900">
                  {{ field.label }}
                </div>

                <div class="text-xs text-surface-switch-500 text-right">
                  Current
                </div>

                @for (item of field.items; track item.persistentId) {
                  <div class="flex justify-between gap-4">
                    <span class="min-w-0 truncate">{{ item.name }}</span>
                    <span class="shrink-0 tabular-nums">
                      {{ item.currentDisplay }}
                    </span>
                  </div>
                }

                <p class="m-0 mt-1 text-surface-switch-900">
                  Change to: {{ field.changeToDisplay }}
                </p>
              </div>
            }
          </div>
        }
      </div>
    }
  </div>

  <ng-template pTemplate="footer">
    <div class="flex justify-end gap-2 pt-2">
      <p-button
        label="Cancel"
        severity="secondary"
        [disabled]="saving"
        (onClick)="close()"
      />
      <p-button
        label="Save"
        icon="pi pi-check"
        [disabled]="!canSave() || saving"
        [loading]="saving"
        (onClick)="save()"
      />
    </div>
  </ng-template>
</p-dialog>
